import { memo, useState } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card } from './BmsCardPrimitives'
import ChartPlaceholder from '../../components/ChartPlaceholder'
import LoadingOverlay from '../../components/LoadingOverlay'
import { formatChartTimestamp, computeAxisTicks } from '../../socket/useTelemetryHistory'
import ChartTooltip from './ChartTooltip'

// Used for the Latest/Min/Max legend cells below — ChartTooltip.jsx has its
// own copy for the hover tooltip itself.
const formatValue = (value, unit) => (value != null ? `${value}${unit}` : '-')

const AXIS_BY_SERIES = {
  SoC: { domain: [0, 100], unit: '%' },
  'Pack Voltage': { domain: [0, 'auto'], unit: 'V' },
  Current: { domain: ['auto', 'auto'], unit: 'A' },
}

// domain: optional [fromMs, toMs] — see TelemetryChart.jsx for why.
// tickCount: target number of x-axis ticks across that domain (default 8).
// isLoading/error: see TelemetryChart.jsx — the plot and its Latest/Min/Max
// panel are replaced by ChartPlaceholder until there are points to draw.
const SocPackVoltageChart = memo(({ chart, height = 260, domain, tickCount = 8, isLoading = false, error = null, onRetry, emptyMessage }) => {
  const [hiddenSeries, setHiddenSeries] = useState([])
  const rangeMs = domain ? domain[1] - domain[0] : undefined
  const formatTick = (epochMs) => formatChartTimestamp(epochMs, rangeMs)
  const axisTicks = domain ? computeAxisTicks(domain[0], domain[1], tickCount) : undefined

  const isEmpty = chart.points.length === 0 || chart.legend.length === 0
  const showError = !isLoading && Boolean(error)

  const toggleSeries = (name) => {
    setHiddenSeries((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]))
  }

  const toggleAll = () => {
    setHiddenSeries((prev) => (prev.length > 0 ? [] : chart.legend.map((s) => s.name)))
  }

  return (
    <Card className="gap-3">
      <span className="text-sm font-medium text-[#252a34] flex items-center gap-1.5">
        <span className="material-icons text-base text-[#616a79]">show_chart</span>
        SoC - Pack Voltage - Current
      </span>

      {isEmpty ? (
        <ChartPlaceholder
          height={height}
          isLoading={isLoading}
          error={showError ? error : null}
          onRetry={onRetry}
          emptyMessage={emptyMessage}
        />
      ) : (
      <div className="relative">
      {isLoading && <LoadingOverlay label="Loading telemetry…" />}
      <div className={`flex gap-8 items-stretch transition-opacity ${isLoading ? 'opacity-50' : ''}`} aria-busy={isLoading}>
        <div className="flex-1 min-w-0">
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chart.points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e8ecf3" vertical={false} />
              <XAxis
                dataKey="epochMs"
                type="number"
                domain={domain ?? ['auto', 'auto']}
                scale="time"
                ticks={axisTicks}
                interval={axisTicks ? 0 : 'preserveStartEnd'}
                tickFormatter={formatTick}
                tick={{ fontSize: 11, fill: '#898781' }}
                axisLine={{ stroke: '#c3c2b7' }}
                tickLine={false}
              />
              {chart.legend.map((s) => {
                const axis = AXIS_BY_SERIES[s.name] ?? { domain: ['auto', 'auto'], unit: '' }
                return (
                  <YAxis
                    key={s.name}
                    yAxisId={s.name}
                    domain={axis.domain}
                    tickCount={6}
                    width={34}
                    tick={{ fontSize: 11, fill: '#898781' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}${axis.unit}`}
                  />
                )
              })}
              {/* z-index so the tooltip isn't painted under the following
                  card once it overflows this one — see TelemetryChart. */}
              <Tooltip content={<ChartTooltip legend={chart.legend} hiddenSeries={hiddenSeries} />} cursor={{ stroke: '#c3c2b7', strokeWidth: 1 }} isAnimationActive={false} offset={28} wrapperStyle={{ zIndex: 30 }} />
              {chart.legend.map((s) => {
                const hidden = hiddenSeries.includes(s.name)
                return (
                  <Line
                    key={s.name}
                    yAxisId={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={hidden ? 'transparent' : s.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={hidden ? false : { r: 4 }}
                    isAnimationActive={false}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flex gap-8 shrink-0 border-l border-[#dfe4ed] pl-6 py-3">
          <div className="flex flex-col gap-1">
            <button
              onClick={toggleAll}
              className="px-1.5 py-0.5 rounded border border-[#dfe4ed] bg-[#f2f4f7] text-xs font-normal text-[#616a79] whitespace-nowrap text-left"
            >
              All Series
            </button>
            {chart.legend.map((s) => {
              const hidden = hiddenSeries.includes(s.name)
              return (
                <button
                  key={s.name}
                  onClick={() => toggleSeries(s.name)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#dfe4ed] bg-[#f9fafa] text-xs font-normal whitespace-nowrap"
                >
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: hidden ? '#c8cfdc' : s.color }} />
                  <span className={hidden ? 'text-[#98a2b3]' : 'text-[#616a79]'}>{s.name}</span>
                </button>
              )
            })}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex gap-4 h-5 items-center text-xs font-medium text-[#616a79]">
              <span className="w-12">Latest</span>
              <span className="w-12">Min</span>
              <span className="w-12">Max</span>
            </div>
            {chart.legend.map((s) => {
              const hidden = hiddenSeries.includes(s.name)
              const cellClass = `w-12 whitespace-nowrap ${hidden ? 'text-[#98a2b3]' : 'text-[#272d37]'}`
              return (
                <div key={s.name} className="flex gap-4 h-5 items-center text-xs font-medium">
                  <span className={cellClass}>{formatValue(s.latest, s.unit ?? ' V')}</span>
                  <span className={cellClass}>{formatValue(s.min, s.unit ?? ' V')}</span>
                  <span className={cellClass}>{formatValue(s.max, s.unit ?? ' V')}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      </div>
      )}
    </Card>
  )
})

export default SocPackVoltageChart
